import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import { Container } from "semantic-ui-react";
import { IPaymentHistoryItem, PaymentModel } from "../../models/PaymentModel";

export const PaymentHistoryPage: React.FC = () => {
  const [payments, setPayments] = useState<IPaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await PaymentModel.getPaymentHistory();
        // The API already sorts newest-first so the page can stay simple and avoid duplicating
        // ordering rules in multiple clients.
        setPayments(data.payments || []);
        setError(null);
      } catch (nextError: any) {
        console.error("Failed to fetch payment history:", nextError);
        setError(nextError.message || "Failed to load payment history");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const goToProfile = () => {
    window.location.href = "/profile";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container style={{ minHeight: "75vh", display: "flex", justifyContent: "center" }}>
      <div style={{ marginTop: "3rem", width: "100%", maxWidth: 900 }}>
        <Typography variant="h3" gutterBottom>
          Payment History
        </Typography>
        <Typography variant="body1" style={{ marginBottom: "1rem" }}>
          Review your completed payments, invoice references, and payment methods.
        </Typography>

        {error && (
          <Paper style={{ padding: "1rem", marginBottom: "1rem" }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        {!error && payments.length === 0 && (
          <Paper style={{ padding: "1rem", marginBottom: "1rem" }}>
            <Typography>No payment history found.</Typography>
          </Paper>
        )}

        {/* Cards keep the page readable on both desktop and narrower profile layouts without adding
            a heavier table implementation for a relatively small record set. */}
        {payments.map((payment) => (
          <Paper key={payment.id} style={{ padding: "1rem", marginBottom: "1rem" }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
              <Box mb={1}>
                <Typography variant="h6">
                  {/* Check payments can be standalone Sales Receipts, so the heading needs to
                      follow the backend-provided document type instead of assuming invoice-only history. */}
                  {payment.documentLabel} #{payment.documentNumber}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {payment.vendorName}
                </Typography>
              </Box>
              <Typography variant="h6">
                ${Number(payment.paymentAmount || 0).toFixed(2)}
              </Typography>
            </Box>

            <Typography variant="body1">
              <strong>Status:</strong> {payment.paymentStatus}
            </Typography>
            <Typography variant="body1">
              <strong>Payment Method:</strong> {payment.paymentMethod}
            </Typography>
            <Typography variant="body1">
              <strong>Payment Date:</strong>{" "}
              {payment.paymentDate
                 ? moment(payment.paymentDate).format("MMM D, YYYY h:mma")
                : "N/A"}
            </Typography>
          </Paper>
        ))}

        <Button variant="outlined" color="primary" onClick={goToProfile}>
          Back to Profile
        </Button>
      </div>
    </Container>
  );
};
